import { createContext } from 'react'
import { Node, Edge, MarkerType } from 'reactflow'
import { STEP_STYLE } from './JourneyEditorUtils'
import { JourneyStepMap } from '../../../types'

interface CreateEdgeParams {
    sourceId: string
    targetId: string
    data: any
    path?: string
}

export const getSourcePath = (handleId: string): string => {
    return handleId.substring(0, handleId.indexOf('-s-'))
}

export const createEdge = ({
    data,
    sourceId,
    targetId,
    path,
}: CreateEdgeParams): Edge => {
    return {
        id: 'e-' + sourceId + '__' + targetId,
        source: sourceId,
        sourceHandle: (path ?? '') + '-s-' + sourceId,
        target: targetId,
        targetHandle: 't-' + targetId,
        data,
        type: STEP_STYLE,
        markerEnd: {
            type: MarkerType.ArrowClosed,
        },
    }
}

/**
 * Returns true if adding an edge source -> target would create a cycle,
 * i.e. the source is already reachable from the target via existing edges.
 * Shared by the per-handle validator and the flow-level isValidConnection
 * (which also covers edge reconnection and drags started from target handles).
 */
export const wouldCreateCycle = (edges: Edge[], source: string | null, target: string | null): boolean => {
    if (!source || !target) return false
    if (source === target) return true

    const visited = new Set<string>()
    const stack = [target]
    while (stack.length) {
        const node = stack.pop()!
        if (node === source) return true
        if (visited.has(node)) continue
        visited.add(node)
        for (const edge of edges) {
            if (edge.source === node && edge.target) {
                stack.push(edge.target)
            }
        }
    }
    return false
}

export type StepStatsMap = Record<number, { delay: number, action: number, completed: number, error: number }>

export type JourneyStatsStatus = 'loading' | 'ready' | 'error'

/**
 * Steps with no activity are omitted from the stats response entirely, so a node
 * without stats is indistinguishable from one that has not loaded yet. Nodes read
 * the fetch status from here to know whether a zero is real.
 */
export const JourneyStatsContext = createContext<JourneyStatsStatus>('ready')

/**
 * Merge a stats map (keyed by step.id) into the existing node list.
 * Used both on initial stats load and after saving a draft (which replaces
 * nodes from the save response, wiping any previously-merged stats).
 */
export const mergeStatsIntoNodes = (nodes: Node[], stats: StepStatsMap): Node[] => {
    return nodes.map(node => {
        const stepStats = node.data?.stepId ? stats[node.data.stepId] : undefined
        if (!stepStats) return node
        return { ...node, data: { ...node.data, stats: stepStats } }
    })
}

export const stepsToNodes = (stepMap: JourneyStepMap, actions: any) => {

    const nodes: Node[] = []
    const edges: Edge[] = []

    for (const [id, { x, y, type, data, name, data_key, children, stats, stats_at, id: stepId }] of Object.entries(stepMap)) {
        nodes.push({
            id,
            position: {
                x,
                y,
            },
            type: 'step',
            data: {
                type,
                name,
                data_key,
                data,
                stats,
                stats_at,
                stepId,
                ...actions,
            },
        })
        children?.forEach(({ external_id, path, data }) => edges.push(createEdge({
            sourceId: id,
            targetId: external_id,
            data,
            path,
        })))
    }

    return { nodes, edges }
}

export const nodesToSteps = (nodes: Node[], edges: Edge[]) => {
    return nodes.reduce<JourneyStepMap>((a, {
        id,
        data: {
            type,
            name = '',
            data_key,
            data = {},
        },
        position: {
            x,
            y,
        },
    }) => {
        a[id] = {
            type,
            data,
            name,
            data_key,
            x,
            y,
            children: edges
                .filter(e => e.source === id)
                .map(({ data = {}, sourceHandle, target }) => ({
                    external_id: target,
                    path: getSourcePath(sourceHandle!),
                    data,
                })),
        }
        return a
    }, {})
}
